import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import actionDeletarRespostaRapida from 'src/actions/dashboard/respostas-rapidas/actionDeletarRespostaRapida';
import actionListaRespostasRapidas from 'src/actions/dashboard/respostas-rapidas/actionListaRespostasRapida';
import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import { Input } from 'src/@/components/ui/input';
import TableRespostasRapidas, { nomeDoArquivo } from './TableRespostasRapidas';
import DrawerFormRespostaRapida from './DrawerFormRespostaRapida';
import D from './RespostasRapidas.design';

const FILTROS = [
  { id: 'todas', label: 'Todas' },
  { id: 'texto', label: 'Texto' },
  { id: 'arquivo', label: 'Arquivo' },
  { id: 'audio', label: 'Áudio' },
];

export default function RespostasRapidasComponent() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
  const [loading, setLoading] = useState(false);

  const [listaRespostaRapidas, setListasRespostasRapidas] = useState([]);
  const [idRespostaRapida, setIdRespostaRapida] = useState(null);

  const [filtroTipo, setFiltroTipo] = useState('todas');
  const [busca, setBusca] = useState('');

  const [drawerMode, setDrawerMode] = useState('criar');
  const [drawerData, setDrawerData] = useState(null);

  const buscarRespostasRapidas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await actionListaRespostasRapidas(user.clienteId);
      setListasRespostasRapidas(res.data.respostas || []);
    } catch (error) {
      console.error('Erro ao buscar Lista de Respostas rápidas', error);
      enqueueSnackbar('Não foi possível carregar as respostas rápidas.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [user.clienteId, enqueueSnackbar]);

  useEffect(() => {
    buscarRespostasRapidas();
  }, [buscarRespostasRapidas]);

  const contagens = useMemo(() => {
    const base = { todas: listaRespostaRapidas.length, texto: 0, arquivo: 0, audio: 0 };
    listaRespostaRapidas.forEach((item) => {
      if (base[item.tipo] !== undefined) base[item.tipo] += 1;
    });
    return base;
  }, [listaRespostaRapidas]);

  const listaVisivel = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return listaRespostaRapidas.filter((item) => {
      if (filtroTipo !== 'todas' && item.tipo !== filtroTipo) return false;
      if (!termo) return true;
      const alvo = `${item.titulo || ''} ${item.resposta || ''} ${nomeDoArquivo(item) || ''}`.toLowerCase();
      return alvo.includes(termo);
    });
  }, [listaRespostaRapidas, filtroTipo, busca]);

  const semNenhuma = !loading && listaRespostaRapidas.length === 0;
  const semResultado = !loading && listaRespostaRapidas.length > 0 && listaVisivel.length === 0;

  const handleOpenDrawer = (actionType = 'criar', data = null) => {
    setDrawerMode(actionType);
    setDrawerData(actionType === 'editar' ? data : null);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
    setDrawerData(null);
  }, []);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await actionDeletarRespostaRapida({ id: idRespostaRapida });
      enqueueSnackbar('Resposta rápida excluída.', { variant: 'info' });
      await buscarRespostasRapidas();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Não foi possível excluir a resposta rápida.', { variant: 'error' });
    } finally {
      setLoading(false);
      setOpenModalConfirmacao(false);
      setIdRespostaRapida(null);
    }
  };

  return (
    <div className={D.root} style={{ fontFamily: D.fontStack }}>
      <div className={D.stickyHeader}>
        <div className={D.container}>
          <div className={D.topbar}>
            <div className={D.topbarIdentity}>
              <div className="min-w-0">
                <h1 className={D.title}>Respostas rápidas</h1>
                <p className={D.subtitle}>
                  Mensagens prontas — texto, arquivo ou áudio — que o time insere direto na conversa.
                </p>
              </div>
            </div>
            <div className={D.topbarActions}>
              <button type="button" onClick={() => handleOpenDrawer('criar')} className={D.primaryButton}>
                <Iconify icon="eva:plus-outline" width={17} />
                Nova resposta
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`${D.container} pt-6 ${D.pageBody}`}>
        <div className={D.stack}>
          <div className={D.controlBar}>
            <div className={D.filterBar}>
              {FILTROS.map((f) => {
                const ativo = filtroTipo === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={ativo}
                    onClick={() => setFiltroTipo(f.id)}
                    className={`${D.filterChip} ${ativo ? D.filterChipOn : D.filterChipOff}`}
                  >
                    {f.label}
                    <span className={ativo ? D.filterCountOn : D.filterCount}>{contagens[f.id]}</span>
                  </button>
                );
              })}
            </div>
            <div className={D.searchWrap}>
              <Iconify icon="eva:search-outline" width={17} className={D.searchIcon} />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por nome ou conteúdo"
                className={D.inputSearch}
              />
            </div>
          </div>

          {loading && listaRespostaRapidas.length === 0 ? (
            <div className={D.loadingBar}>
              <div className={D.loadingBarInner} style={{ width: '40%' }} />
            </div>
          ) : (
            <div className={D.cardFlush}>
              {semNenhuma && (
                <div className={D.emptyWrap}>
                  <div className={D.emptyIcon}>
                    <Iconify icon="eva:flash-outline" width={24} />
                  </div>
                  <p className={D.emptyTitle}>Nenhuma resposta rápida ainda</p>
                  <p className={D.emptyDesc}>
                    Salve as mensagens que o time mais repete e envie qualquer uma delas em um clique.
                  </p>
                  <button type="button" onClick={() => handleOpenDrawer('criar')} className={`${D.link} mt-4`}>
                    Criar a primeira
                  </button>
                </div>
              )}

              {semResultado && (
                <div className={D.emptyWrap}>
                  <div className={D.emptyIcon}>
                    <Iconify icon="eva:search-outline" width={24} />
                  </div>
                  <p className={D.emptyTitle}>Nada encontrado</p>
                  <p className={D.emptyDesc}>Nenhuma resposta combina com a busca e o tipo selecionados.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setBusca('');
                      setFiltroTipo('todas');
                    }}
                    className={`${D.link} mt-4`}
                  >
                    Limpar filtros
                  </button>
                </div>
              )}

              {listaVisivel.length > 0 && (
                <TableRespostasRapidas
                  data={listaVisivel}
                  onEdit={(row) => handleOpenDrawer('editar', row)}
                  onDelete={(row) => {
                    setIdRespostaRapida(row._id);
                    setOpenModalConfirmacao(true);
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <ModalConfimacao
        open={openModalConfirmacao}
        onClose={() => setOpenModalConfirmacao(false)}
        onConfirm={handleDelete}
        loadingFinalizar={loading}
        mensagem={'Excluir esta resposta rápida? Ela sai da lista do chat e a ação não pode ser desfeita.'}
      />

      <DrawerFormRespostaRapida
        open={openDrawer}
        onClose={handleCloseDrawer}
        mode={drawerMode}
        initialData={drawerData}
        clienteId={user.clienteId}
        onSuccess={buscarRespostasRapidas}
        onRequestDelete={(id) => {
          setIdRespostaRapida(id);
          setOpenModalConfirmacao(true);
        }}
      />
    </div>
  );
}
